import { CardSimIcon, CheckCheckIcon, WebhookIcon, type LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { HiisMark } from "@/brand/HiisMark"
import { Wordmark } from "@/brand/Wordmark"

/** The request the picture shows: the API's own path, the same in every language. */
const SEND_REQUEST = "POST /api/v2/messages"

const FEATURES: readonly { key: "sims" | "api" | "delivery"; Icon: LucideIcon }[] = [
  { key: "sims", Icon: CardSimIcon },
  { key: "api", Icon: WebhookIcon },
  { key: "delivery", Icon: CheckCheckIcon },
]

/** A message on the phone: lines of text drawn as bars, ticked when it is ours. */
function Bubble({ out, widths }: { out?: boolean; widths: readonly string[] }) {
  return (
    <div
      className={`auth-bubble space-y-1.5 rounded-2xl px-3 py-2.5 ${
        out ? "auth-bubble-out self-end rounded-br-md" : "auth-bubble-in self-start rounded-bl-md"
      }`}
      style={{ width: out ? "72%" : "62%" }}
    >
      {widths.map((w) => (
        <span key={w} className="auth-bar" style={{ width: w }} />
      ))}
      {out ? <CheckCheckIcon className="ml-auto size-3.5 opacity-90" /> : null}
    </div>
  )
}

/**
 * The phone, sending: a conversation that writes itself (the motion stops
 * under reduced motion and shows it whole), the request that sent it and its
 * delivery. Pictures only, beside the words that say the same.
 */
function PhoneScene() {
  const { t } = useTranslation("common")
  return (
    <div aria-hidden="true" className="relative mx-auto w-54 shrink-0">
      <div className="auth-phone relative flex h-88 flex-col p-3">
        <span className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-white/25" />
        <div className="mb-3 flex items-center gap-2 px-1">
          <span className="auth-badge size-7 rounded-full" />
          <span className="flex-1 space-y-1.5 text-white">
            <span className="auth-bar" style={{ width: "60%" }} />
            <span className="auth-bar" style={{ width: "35%", opacity: 0.3 }} />
          </span>
          <span className="auth-pulse size-2 rounded-full bg-success" />
        </div>
        <div className="flex flex-1 flex-col gap-2.5 overflow-hidden px-1">
          <Bubble out widths={["100%", "70%"]} />
          <Bubble widths={["100%", "45%"]} />
          <Bubble out widths={["100%", "85%", "40%"]} />
          <Bubble widths={["80%"]} />
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-full bg-white/10 p-1.5 pl-3">
          <span className="auth-bar flex-1 text-white" style={{ opacity: 0.25 }} />
          <span className="auth-badge size-6 rounded-full" />
        </div>
      </div>
      <div className="auth-glass auth-float absolute -top-5 -right-8 flex items-center gap-2 rounded-xl px-3 py-2 font-mono auth-micro whitespace-nowrap">
        <span className="rounded bg-success/25 px-1.5 py-0.5 font-semibold text-white">202</span>
        <span>{SEND_REQUEST}</span>
      </div>
      <div className="auth-glass auth-float auth-float-late absolute -right-14 bottom-16 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium whitespace-nowrap">
        <CheckCheckIcon className="size-4 text-success" />
        {t("authShowcase.delivered")}
      </div>
    </div>
  )
}

/**
 * The brand panel beside the auth screens on wide screens (src/theme/auth.css):
 * what the product does, in a sentence, three points and a picture. Static
 * markup only, as index.html paints it too (src/app/boot-screen.test.tsx).
 *
 * The panel runs the page's full height (a long form scrolls past one
 * screen); its content stays pinned to the window. `overflow-clip`, not
 * `-hidden`, which would make the panel a scroll container and unpin it.
 */
export function AuthShowcase() {
  const { t } = useTranslation("common")
  return (
    <aside className="auth-showcase relative hidden overflow-clip lg:block">
      <div aria-hidden="true" className="auth-showcase-grid absolute inset-0" />
      <div
        aria-hidden="true"
        className="auth-aurora auth-aurora-a absolute -top-32 -left-24 size-104"
      />
      <div
        aria-hidden="true"
        className="auth-aurora auth-aurora-b absolute -right-32 -bottom-24 size-120"
      />
      <div className="sticky top-0 flex h-dvh min-h-160 flex-col px-12 py-10 xl:px-16">
        <Wordmark size="lg" className="text-lg font-semibold tracking-tight" />
        <div className="my-auto max-w-xl pt-10">
          <p className="auth-glass mb-5 inline-block rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase">
            {t("authShowcase.eyebrow")}
          </p>
          <p className="text-3xl leading-tight font-semibold tracking-tight text-balance xl:text-4xl">
            {t("authShowcase.headline")}
          </p>
          <p className="auth-showcase-muted mt-4 text-base leading-relaxed text-pretty">
            {t("authShowcase.lead")}
          </p>
          <div className="mt-10 flex items-center gap-10">
            <ul className="min-w-0 flex-1 space-y-5">
              {FEATURES.map(({ key, Icon }) => (
                <li key={key} className="flex gap-3">
                  <span className="auth-feature-icon flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <Icon aria-hidden="true" className="size-4.5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">
                      {t(`authShowcase.features.${key}.title`)}
                    </span>
                    <span className="auth-showcase-muted block text-sm">
                      {t(`authShowcase.features.${key}.body`)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="hidden xl:block">
              <PhoneScene />
            </div>
          </div>
        </div>
        <HiisMark className="self-start" />
      </div>
    </aside>
  )
}

/** The panel's three points on narrow screens, where the panel does not show: a row under the form. */
export function AuthFeatureChips() {
  const { t } = useTranslation("common")
  return (
    <div className="mt-8 w-full max-w-sm lg:hidden">
      <ul className="grid grid-cols-3 gap-2">
        {FEATURES.map(({ key, Icon }) => (
          <li
            key={key}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card/70 px-2 py-3 text-center auth-micro leading-tight font-medium text-muted-foreground"
          >
            <Icon aria-hidden="true" className="size-4 text-primary" />
            {t(`authShowcase.features.${key}.title`)}
          </li>
        ))}
      </ul>
    </div>
  )
}
